import React from 'react'
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  alpha,
  useTheme
} from '@mui/material'
import {
  Email,
  ViewKanban,
  Group,
  Analytics,
  Security,
  IntegrationInstructions,
  ArrowForward,
  CheckCircle
} from '@mui/icons-material'

const features = [
  {
    icon: <Email />,
    title: 'Reliable Email-to-Ticket',
    description: 'Every email becomes a ticket. No missed requests, no manual entry.'
  },
  {
    icon: <Group />,
    title: 'Team-Based Visibility',
    description: 'Built for shift work and shared responsibility. Everyone on the team sees progress—no more "off-shift" blind spots.'
  },
  {
    icon: <Analytics />,
    title: 'Reporting That Works',
    description: 'Response times, ticket volume, agent performance—actual insights, not vanity metrics. No more exporting to Excel.'
  },
  {
    icon: <IntegrationInstructions />,
    title: 'Microsoft Teams Integration',
    description: 'Get notified where you already work. No more checking another dashboard.'
  },
  {
    icon: <Security />,
    title: 'Azure AD Authentication',
    description: 'Your team logs in with their Microsoft account. One less password to manage.'
  },
  {
    icon: <ViewKanban />,
    title: 'Multiple Views',
    description: 'List, Kanban, or tiles—work the way your team works best.'
  }
]

const benefits = [
  'No feature bloat - just what you need',
  'Reporting that provides actual insights (no more Excel exports)',
  'Built for teams with shift work and shared responsibility',
  'Microsoft Teams integration included',
  'Azure AD single sign-on',
  'Organisation hierarchy for multi-location oversight',
  'Multiple views: List, Kanban, Tiles',
  'No contracts, no commitments—cancel anytime'
]

export default function HomePage() {
  const theme = useTheme()

  const handleGetStarted = () => {
    window.location.href = '/signup'
  }

  const handleLearnMore = () => {
    document.getElementById('problems')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: 12,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h1" component="h1" gutterBottom sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' }, fontWeight: 'bold' }}>
                Helpdesk Software That Actually Works
              </Typography>
              <Typography variant="h5" component="h2" gutterBottom sx={{ opacity: 0.9, mb: 4 }}>
                Built for Microsoft-first IT teams. No feature bloat, no terrible reporting, no poor integration. Just the essentials done right.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={handleGetStarted}
                  sx={{
                    bgcolor: 'white',
                    color: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: alpha('#ffffff', 0.9)
                    },
                    py: 1.5,
                    px: 4
                  }}
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleLearnMore}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: alpha('#ffffff', 0.1)
                    },
                    py: 1.5,
                    px: 4
                  }}
                >
                  Learn More
                </Button>
              </Stack>
              <Stack direction="row" spacing={3} alignItems="center" sx={{ opacity: 0.9 }}>
                <Typography variant="body2">
                  ✓ 14-day free trial
                </Typography>
                <Typography variant="body2">
                  ✓ No credit card required
                </Typography>
                <Typography variant="body2">
                  ✓ Setup in 5 minutes
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 2,
                  p: 3,
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Simple, Transparent Pricing
                </Typography>
                <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 2 }}>
                  <Typography variant="h3" component="span" sx={{ fontWeight: 'bold' }}>
                    £25
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.8 }}>
                    per agent per month
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                  Billed monthly • Cancel anytime
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  All features included • No hidden fees • No contracts
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Problem/Solution Section */}
      <Box id="problems" sx={{ py: 12 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" sx={{ mb: 8 }}>
            <Typography variant="h2" component="h2" gutterBottom sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, fontWeight: 'bold' }}>
              Tired of Paying for 100 Features You Never Use?
            </Typography>
          </Box>
          <Grid container spacing={4} sx={{ mb: 6 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'error.main' }}>
                  Feature Bloat
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your helpdesk has AI chatbots, field service, and social media integration. You just need reliable ticket management.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'error.main' }}>
                  Terrible Reporting
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Still exporting to Excel because the built-in reports are useless? We've been there.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'error.main' }}>
                  Poor Integration
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Another tab to check. Another login to remember. Another tool that doesn't talk to Teams.
                </Typography>
              </Card>
            </Grid>
          </Grid>
          <Box textAlign="center">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, maxWidth: 800, mx: 'auto' }}>
              We stripped out 80% of the features you never use, and made the 20% you actually need work perfectly.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box id="features" sx={{ py: 12, bgcolor: 'grey.50' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" sx={{ mb: 8 }}>
            <Typography variant="h2" component="h2" gutterBottom sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, fontWeight: 'bold' }}>
              Everything You Need, Nothing You Don't
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Built by an IT professional who understands what really matters in helpdesk software
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8]
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2
                      }}
                    >
                      {React.cloneElement(feature.icon, {
                        sx: { color: theme.palette.primary.main, fontSize: 28 }
                      })}
                    </Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: 12 }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Why IT Teams Choose Us
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
                Built by an IT professional who was tired of paying for bloated helpdesk tools with terrible reporting. 
                We focus on what actually matters: reliable ticket management, visibility for your whole team, and reporting that actually works.
              </Typography>
              <Stack spacing={2}>
                {benefits.map((benefit, index) => (
                  <Stack direction="row" spacing={2} alignItems="center" key={index}>
                    <CheckCircle sx={{ color: theme.palette.success.main }} />
                    <Typography variant="body1">{benefit}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center'
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Ready to Get Started?
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Join the growing number of IT teams who've discovered a simpler way to manage support tickets.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={handleGetStarted}
                  sx={{ py: 1.5, px: 4 }}
                >
                  Start Your Free Trial
                </Button>
                <Typography variant="caption" display="block" sx={{ mt: 2, opacity: 0.7 }}>
                  No credit card required • 14-day free trial • Cancel anytime
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Who It's For Section */}
      <Box sx={{ py: 12, bgcolor: 'grey.50' }}>
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
              Built for Microsoft-First IT Teams
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Perfect for:
                  </Typography>
                  <Stack spacing={1} alignItems="flex-start">
                    <Typography variant="body2">• 10-100 person companies</Typography>
                    <Typography variant="body2">• Multi-location operations</Typography>
                    <Typography variant="body2">• Teams on Microsoft 365</Typography>
                    <Typography variant="body2">• Shift-based support teams</Typography>
                  </Stack>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Ideal for:
                  </Typography>
                  <Stack spacing={1} alignItems="flex-start">
                    <Typography variant="body2">• IT departments</Typography>
                    <Typography variant="body2">• Library systems</Typography>
                    <Typography variant="body2">• Retail chains</Typography>
                    <Typography variant="body2">• Branch operations</Typography>
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 12 }}>
        <Container maxWidth="md">
          <Box textAlign="center" sx={{ mb: 8 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Frequently Asked Questions
            </Typography>
          </Box>
          <Stack spacing={3}>
            {[
              {
                question: 'How quickly can we get set up?',
                answer: 'Most teams are up and running in under 5 minutes. Our setup wizard guides you through creating your organisation, inviting team members, and configuring email integration.'
              },
              {
                question: 'Is this built for Microsoft-first companies?',
                answer: 'Absolutely. We integrate natively with Azure AD, Microsoft Teams, and Microsoft 365. If your company runs on the Microsoft stack, this helpdesk was built with you in mind.'
              },
              {
                question: 'Can we integrate with Microsoft Teams?',
                answer: 'Yes! Teams integration is included with every plan. Get real-time notifications for new tickets, status updates, and team assignments directly in your Teams channels.'
              },
              {
                question: 'How does team visibility work?',
                answer: 'Unlike traditional helpdesks where only the ticket creator can see progress, our organisation-based system lets everyone on your team see all tickets. Perfect for shift work, libraries, and multi-location operations where handoffs are common.'
              },
              {
                question: 'What happens after the 14-day trial?',
                answer: 'If you decide to continue, you\'ll be charged £25 per agent per month, billed monthly. No contracts, no commitments—cancel anytime. If not, your account will be paused and your data kept safe for 30 days in case you change your mind.'
              }
            ].map((faq, index) => (
              <Card key={index}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {faq.question}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {faq.answer}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => window.location.href = '/faq'}
            >
              View All FAQs
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CTA Footer */}
      <Box
        sx={{
          bgcolor: theme.palette.primary.main,
          color: 'white',
          py: 8,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Ready to Transform Your Support Process?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Built for 10-100 person companies on the Microsoft stack who value simplicity over feature bloat.
          </Typography>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForward />}
            onClick={handleGetStarted}
            sx={{
              bgcolor: 'white',
              color: theme.palette.primary.main,
              '&:hover': {
                bgcolor: alpha('#ffffff', 0.9)
              },
              py: 1.5,
              px: 4,
              fontSize: '1.1rem'
            }}
          >
            Start Your Free Trial Today
          </Button>
          <Typography variant="body2" sx={{ mt: 2, opacity: 0.8 }}>
            14-day free trial • No credit card required • Setup in 5 minutes
          </Typography>
        </Container>
      </Box>
    </Box>
  )
}